import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { toast } from "../../../utils/notifications";
import TabNavigation from "./TabNavigation";
import QuoteHistorySidebar from "./QuoteHistorySidebar";
import { RFQTab } from "./TabNavigation";
import { useUser } from "../../../contexts/UserContext";
import UserSelector from "./UserSelector";
import { FailedRfqAlertCard } from "../../../components/shared";
import { useRealtimeSync } from "../../../hooks/useRealtimeSync";
import { Page, PageHeader } from "../../layout/Page";
import { queryKeys } from "../../../api/queryKeys";
import { useRfqListQuery } from "./rfq-list/useRfqListQuery";
import { useRfqCounts } from "./rfq-list/useRfqCounts";
import { useRfqSelection } from "./rfq-list/useRfqSelection";
import { useRfqDialogs } from "./rfq-list/useRfqDialogs";
import { useCheckEmails } from "./rfq-list/useCheckEmails";
import RfqListToolbar from "./rfq-list/RfqListToolbar";
import RfqTable from "./rfq-list/RfqTable";
import RfqDialogs from "./rfq-list/RfqDialogs";
import { RfqListConnectionError, RfqListLoading, RfqListNoData } from "./rfq-list/RfqListStates";

/**
 * The RFQ list page: one tab of RFQs (New / Manual / Quoted), searchable,
 * with each RFQ expanding into its items. Data: useRfqListQuery and
 * useRfqCounts; ticked RFQs: useRfqSelection; delete, blacklist and
 * attachment dialogs: useRfqDialogs + RfqDialogs.
 */
export default function RFQList() {
  const [activeTab, setActiveTab] = useState<RFQTab>("NEW");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expandedRfqId, setExpandedRfqId] = useState<number | null>(null);
  // Quote history sidebar state
  const [sidebarMinimized, setSidebarMinimized] = useState(true);

  // Toggle expanded RFQ
  const handleToggleExpanded = (rfqId: number) => {
    setExpandedRfqId(expandedRfqId === rfqId ? null : rfqId);
    // Reset sidebar to minimized when switching RFQs
    if (expandedRfqId !== rfqId) {
      setSidebarMinimized(true);
    }
  };

  // Deep link from the Tracking page's open queue: /?rfq=<id>&tab=NEW opens
  // that tab with the RFQ expanded.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'NEW' || tab === 'DIY' || tab === 'QUOTED') setActiveTab(tab);
    const rfqId = Number(searchParams.get('rfq'));
    if (Number.isInteger(rfqId) && rfqId > 0) setExpandedRfqId(rfqId);
  }, [searchParams]);

  const queryClient = useQueryClient();
  const { selectedUser } = useUser();

  // Real-time sync, so changes by other users appear without a refresh and
  // two people do not quote the same RFQ at once.
  const { isConnected: isRealtimeConnected } = useRealtimeSync({
    tables: ['rfqs', 'rfq_items'],
    debounceMs: 500,
  });

  const list = useRfqListQuery(activeTab, searchKeyword, selectedUser);
  const counts = useRfqCounts(selectedUser?.name);
  const selection = useRfqSelection(list.sortedRFQs);
  const dialogs = useRfqDialogs({ rfqs: list.filteredRFQs, selection, userName: selectedUser?.name });
  const { isRefreshing, lastCheckedTime, handleCheckEmails } = useCheckEmails();

  const handleTabChange = (tab: RFQTab) => {
    setActiveTab(tab);
    setSearchKeyword("");
    selection.clearSelection();

    if (tab === "DIY" && !selectedUser?.name) {
      toast({
        title: "Warning",
        description: "Please select a user to view DIY RFQs",
        severity: "warning",
      });
    }
  };

  // Refresh the tab and the counts
  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.tab(activeTab) });
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqCounts.all });
    selection.clearSelection();
    setSuccessMessage("Data refreshed successfully");
  };

  if (list.isLoading) return <RfqListLoading />;

  if (list.error && !list.data) {
    return <RfqListConnectionError onRetry={refreshData} />;
  }

  if (!list.data) {
    return <RfqListNoData onRefresh={refreshData} onCheckEmails={handleCheckEmails} isRefreshing={isRefreshing} />;
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Quote History Sidebar */}
      {expandedRfqId && (
        <QuoteHistorySidebar
          currentRfqId={expandedRfqId}
          isMinimized={sidebarMinimized}
          onToggleMinimized={() => setSidebarMinimized(!sidebarMinimized)}
        />
      )}

      {/* Main Content */}
      <Page width="full">
      <PageHeader
        title="RFQ Management"
        actions={
          <>
            <UserSelector />
            <FailedRfqAlertCard />
          </>
        }
      />

      <TabNavigation
        activeTab={activeTab}
        onChange={handleTabChange}
        counts={counts}
      />

      <RfqListToolbar
        searchKeyword={searchKeyword}
        onSearchChange={setSearchKeyword}
        onCheckEmails={handleCheckEmails}
        isRefreshing={isRefreshing}
        lastCheckedTime={lastCheckedTime}
        isUpdating={list.isFetching && !list.isLoading}
        isRealtimeConnected={isRealtimeConnected}
      />

      {/* Error message display if data fetch failed */}
      {list.errorMessage && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }} onClose={() => list.setErrorMessage("")}>
          {list.errorMessage}
        </Alert>
      )}

      {/* Success message display */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 1 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      <RfqTable
        rfqs={list.sortedRFQs}
        activeTab={activeTab}
        selection={selection}
        expandedRfqId={expandedRfqId}
        isRefreshing={isRefreshing}
        onRefresh={refreshData}
        onCheckEmails={handleCheckEmails}
        onMassDelete={dialogs.openMassDelete}
        onDelete={dialogs.openDelete}
        onViewAttachments={dialogs.openAttachments}
        onToggleExpanded={handleToggleExpanded}
        onBlacklist={dialogs.openBlacklist}
      />

      <RfqDialogs dialogs={dialogs} />
      </Page>
    </Box>
  );
}
